import { useCallback, useContext, useMemo, type MouseEvent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Clickable from 'components/Common/Clickable'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import ModalContext from 'contexts/ModalContext'
import FormatCurrency from 'components/Common/FormatCurrency'
import offerPageURL from 'lib/offer/offerPageURL'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import useLuxPlusOfferVoucher from 'luxPlus/hooks/useLuxPlusOfferVoucher'
import LuxPlusOfferVoucherModal from './LuxPlusOfferVoucherModal'
import SolidTicketIcon from 'LuxKit/Icons/solid/SolidTicketIcon'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'

export interface LuxPlusOfferVoucherCalloutProps {
  offer: App.AnyOffer
  mode: 'search' | 'offer'
  align?: 'start' | 'center'
}

function LuxPlusOfferVoucherCallout({ offer, mode, align = 'center' }: LuxPlusOfferVoucherCalloutProps) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const voucher = useLuxPlusOfferVoucher([offer.id])
  const accentColour = useRebrandSuccessDarkColour()
  const primaryActionProps = useMemo(() => mode === 'search' ? {
    children: intl.formatMessage({
      id: 'pH5nEsH_5S6jEPdKoJxd',
      defaultMessage: 'Continue to offer',
      description: 'Primary action that continues from a LuxPlus voucher callout to the offer page',
    }),
    to: offerPageURL(offer),
  } : undefined, [intl, offer, mode])

  const openModal = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    // Prevent linked offer cards from navigating away when clicking the info button
    event.preventDefault()
    event.stopPropagation()

    if (voucher) {
      showModal(
        <LuxPlusOfferVoucherModal
          voucherAmount={voucher.voucherAmount}
          primaryActionProps={primaryActionProps}
        />,
      )
    }
  }, [primaryActionProps, showModal, voucher])

  if (!voucher) return undefined

  return <BodyTextBlock
    variant="small"
    colour={accentColour}
    align={align}
    startIcon={<SolidTicketIcon />}
    endIcon={<Clickable onClick={openModal}><LineInfoCircleIcon size="XS" colour="light-foreground-alt" /></Clickable>}
  >
    <FormattedMessage
      id="2liG4ufe_s-856AiHZGD"
      defaultMessage="Extra {amount} off with {programName}"
      description="LuxPlus+ offer price callout promoting an extra voucher discount available after joining the membership"
      values={{ amount: <FormatCurrency format="rounded" value={voucher.voucherAmount} />, programName: LUXURY_PLUS.PROGRAM_NAME }}
    />
  </BodyTextBlock>
}

export default LuxPlusOfferVoucherCallout
